import { get, head, isEmpty, isNil, map } from 'lodash';
import { Injectable } from '@angular/core';

import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import { Observable, observableIIF, observableOf, rx } from '@app/shared/rx';
import { batchRequestMap } from '@app/shared/rx-operators';
import { IBaseEntityType } from '@app/shared/types/entities/base/base.type';
import { EntityId, ObjectId } from '@app/shared/types/entities/entity-id.type';
import { IStandWorkSheetItem } from '@app/shared/types/entities/stand-worksheet/stand-worksheet.type';
import { IEvent } from '@app/shared/types/entities/xprt-entities/events/events.type';
import { ENTITY_ID_PATH } from '@config/global-constants';

import { EventsService } from '../../events/events.service';
import { StandWorksheetCalcServiceProvider } from '../../stand-worksheet/calculation-services/stand-worksheet-calc-service-provider';
import { StandWorksheetService } from '../../stand-worksheet/stand-worksheet.service';
import { IReplenishStrategyParams } from '../replenish.types';
import { AbstractReplenishDataService } from './abstract-replenish-data.service';
import { ReplenishDataSource } from './replenish-data.enums';
import { createStandWorksheetItemsMap } from './utils/stand-worksheet-data.utils';

@Injectable()
class ReplenishStandWorksheetDataService extends AbstractReplenishDataService<
  Record<EntityId, Partial<IStandWorkSheetItem>>
> {
  public static SOURCE_NAME: string = ReplenishDataSource.StandWorksheet;

  @lazyInject(EventsService) public eventsService: EventsService;
  @lazyInject(StandWorksheetService)
  public standWorksheetService: StandWorksheetService;
  @lazyInject(StandWorksheetCalcServiceProvider)
  public standWorksheetCalcServiceProvider: StandWorksheetCalcServiceProvider;

  public fetch(
    params: IReplenishStrategyParams,
  ): Observable<Record<EntityId, Partial<IStandWorkSheetItem>>> {
    if (isEmpty(params.percentDesMap)) {
      return observableOf({});
    }

    const toSiteIds = map(
      params.percentDesMap,
      (destination) => destination.toSite,
    );

    const batchRequestCount = 100;

    return batchRequestMap(
      toSiteIds,
      (toSiteId) => {
        return this.getLastEventBySiteId(toSiteId).pipe(
          rx.first(),
          rx.switchMap((recentEventOfDestination) =>
            observableIIF(
              () => !isNil(recentEventOfDestination),
              this.getRecentStandWorksheet(
                get(recentEventOfDestination, 'id'),
                toSiteId,
              ).pipe(
                rx.switchMap((recentStandWorksheet) =>
                  observableIIF(
                    () => !isNil(recentStandWorksheet),
                    this.getRecentSWSItems(recentStandWorksheet).pipe(
                      rx.map((swsItems) => ({
                        recentEvent: recentEventOfDestination,
                        itemsMap: swsItems,
                      })),
                    ),
                    observableOf({
                      recentEvent: recentEventOfDestination,
                    }),
                  ),
                ),
              ),
              observableOf(null),
            ),
          ),
          rx.filter((itemsMap) => !isEmpty(itemsMap)),
          rx.map((itemsMap) => ({ [toSiteId]: itemsMap })),
        );
      },
      batchRequestCount,
    ).pipe(
      rx.reduce(
        (totalSWSItemsMap, curSWSItemsMap) => ({
          ...totalSWSItemsMap,
          ...curSWSItemsMap,
        }),
        {},
      ),
    );
  }

  public getLastEventBySiteId(siteId: ObjectId): Observable<Partial<IEvent>> {
    return this.eventsService
      .fetchList({
        filter: `status eq in(open,closed)) and ((availability_conditions/condition eq 'all') or ((availability_conditions/condition eq 'include') and (availability_conditions/sites eq in(${siteId}))) or ((availability_conditions/condition eq 'exclude') and (availability_conditions/sites ne in(${siteId})))`,
        select: ['status', 'id'],
        orderby: `event_days/start_date_time desc`,
        top: 1,
      })
      .pipe(
        rx.map((response) => {
          return head(response.items);
        }),
      );
  }

  public getRecentStandWorksheet(
    eventId: ObjectId,
    siteId: ObjectId,
  ): Observable<IBaseEntityType> {
    return this.standWorksheetService
      .fetchList({
        filter: `event_id eq objectid(${eventId}) and site_id eq objectid(${siteId})`,
        orderby: 'updated_at desc',
        select: ['entity_id', 'formula_version'],
        top: 1,
      })
      .pipe(rx.map((response) => head(response.items)));
  }

  public getStandWorksheetItemsById(
    entityId: ObjectId,
  ): Observable<IStandWorkSheetItem[]> {
    return this.standWorksheetService.getStandSheetItemList({
      filter: `stand_work_sheet_entity_id eq objectid(${entityId})`,
      select: [
        'manager_begin',
        'begin',
        'pre_event_in',
        'end',
        'during_event_in',
        'during_event_out',
        'adjustment',
        'gratis',
        'ingredient_spoil',
        'sale',
        'sale_post_closure',
        'inventory_item_entity_id',
      ],
    });
  }

  public getRecentSWSItems(
    recentStandWorksheet: IBaseEntityType,
  ): Observable<IStandWorkSheetItem> {
    return this.getStandWorksheetItemsById(
      get(recentStandWorksheet, ENTITY_ID_PATH),
    ).pipe(
      rx.map((swsItems) => {
        const swCalculationService =
          this.standWorksheetCalcServiceProvider.getService(
            recentStandWorksheet?.formula_version,
          );

        return createStandWorksheetItemsMap(swsItems, swCalculationService);
      }),
    );
  }
}

export { ReplenishStandWorksheetDataService };
